<script lang="ts" setup>
import { useGoodsStore } from '~/store/store-goods/goods'
import { commaizeNumber } from '@/utils/primitive/number'
import { CardBnftListItem } from '~/api/types/goods'
import { useBenefit } from '~/pages/goods/composables/ui'

interface Reactive {
  isShowCoupon: boolean
  isShowToggle: boolean
}

const goodsStore = useGoodsStore()
const { goods } = storeToRefs(goodsStore)
const { goodsCoupon, marketingCoupon } = useBenefit()

const state = reactive<Reactive>({
  isShowCoupon: false,
  isShowToggle: false
})
const totalDcPrice = ref<number>(0)

/** 최대혜택가 */
const benefitPrice = computed(() => {
  return commaizeNumber(Number(goods.value?.detailInfo.salePrice) - totalDcPrice.value)
})

/** 쇼핑북 할인 */
const sbDcPrice = computed(() => Number(goods.value?.promotionInfo?.sbDcAmt))

/** 일시불 할인 */
const lspPrice = computed(() => {
  const promotionInfo = goods.value?.promotionInfo
  const lspAmt = Number(promotionInfo?.lspAmt)
  if (lspAmt && lspAmt > 0) {
    return promotionInfo?.lspAmt
  }
})

/** 할인가 */
const dcPriceInfo = computed(() => {
  const salePrice = goods.value?.detailInfo?.salePrice
  const promotionInfo = goods.value?.promotionInfo
  const goodsAmt = goodsCoupon(goods.value!)[1] ? Number(goodsCoupon(goods.value!)[1].replaceAll(/,/g, '')) : 0
  const fullAmt = Number(promotionInfo?.lspAmt)
  const marketingAmt = marketingCoupon(goods.value!)[1]
    ? Number(marketingCoupon(goods.value!)[1].replaceAll(/,/g, ''))
    : 0
  const sbAmt = Number(promotionInfo?.sbDcAmt)
  let cardAmt = 0
  if (promotionInfo?.cardBnftList && promotionInfo?.cardBnftList.length > 0) {
    if (salePrice && salePrice >= promotionInfo?.cardBnftList?.[0].amtStartVal) {
      if (promotionInfo?.cardBnftList[0].maxBnftLimit <= promotionInfo?.cardBnftList[0].realCardVal) {
        cardAmt = promotionInfo?.cardBnftList[0].maxBnftLimit
      } else {
        cardAmt = promotionInfo?.cardBnftList[0].realCardVal
      }
    }
  }
  totalDcPrice.value = goodsAmt + fullAmt + marketingAmt + sbAmt + cardAmt
  return totalDcPrice.value
})

/** 카드 혜택 */
const cardBenefit = computed(() => {
  const cardList = goods.value?.promotionInfo?.cardBnftList
  if (cardList && cardList.length > 0) {
    const salePrice = goods.value?.detailInfo?.salePrice
    if (Number(salePrice) >= Number(cardList[0].amtStartVal)) {
      return true
    }
  }
})

const offerNm = computed(() => {
  const cardList = goods.value?.promotionInfo?.cardBnftList
  if (cardList) {
    return `${cardList[0].cardBnft} ${cardList[0].bnftType === '31' ? '즉시' : '청구'} 할인 ${cardList[0].cardBnftVal}%`
  }
})

/** 대표 금액제한 조건 */
const amtStartVal = computed(() => {
  const cardList = goods.value?.promotionInfo?.cardBnftList
  if (cardList && cardList.length > 0 && !cardBenefit.value) {
    return `(${commaizeNumber(cardList[0].amtStartVal)}원 이상)`
  }
})

/** 카드 할인가 */
const realCardVal = computed(() => {
  const cardList = goods.value?.promotionInfo?.cardBnftList
  if (cardList && cardList.length > 0 && cardBenefit.value) {
    if (cardList[0].maxBnftLimit <= cardList[0].realCardVal) {
      return commaizeNumber(cardList[0].maxBnftLimit)
    } else {
      return commaizeNumber(cardList[0].realCardVal)
    }
  }
})

function cardOfferInfo(item: CardBnftListItem) {
  return `${item.cardBnft} ${item.bnftType === '31' ? '즉시' : '청구'} 할인 ${item.cardBnftVal}%`
}

function cardOptionInfo(item: CardBnftListItem) {
  if (item.amtStartVal > 0 && item.maxBnftLimit === 0) {
    return `${commaizeNumber(item.amtStartVal)}원 이상 결제 시 할인`
  } else if (item.amtStartVal > 0 && item.maxBnftLimit > 0) {
    return `${commaizeNumber(item.amtStartVal)}원 이상 결제 시 최대 ${commaizeNumber(item.maxBnftLimit)}원 할인`
  }
}

function isOpenCardBenefit(state: boolean) {
  return {
    'btn-toggle active': state,
    'btn-toggle': !state
  }
}

function _onClickToggleButton() {
  state.isShowToggle = !state.isShowToggle
}
</script>

<template>
  <!-- [D]: 그룹상품 핵심BM 하위상품 상세 팝업 일때 비노출 -->
  <div class="benefit-info-wrap">
    <dl class="best-price-info">
      <dt>최대 혜택가</dt>
      <dd>
        <em>{{ benefitPrice }}</em
        >원{{ goods?.detailInfo?.prcWaveDisp }}
      </dd>
    </dl>

    <div class="benefit-info">
      <dl class="dc-price-info">
        <dt>총 가격할인</dt>
        <dd v-if="Number(dcPriceInfo) > 0">
          <em>-{{ commaizeNumber(dcPriceInfo) }}</em
          >원{{ goods?.detailInfo?.prcWaveDisp }}
        </dd>
      </dl>

      <div class="benefit-list">
        <dl v-if="goodsCoupon(goods!).length > 0">
          <dt>상품 {{ goodsCoupon(goods!)[0] }} 할인</dt>
          <dd>
            <em>-{{ goodsCoupon(goods!)[1] }}</em
            >원{{ goods?.detailInfo?.prcWaveDisp }}
          </dd>
        </dl>
        <dl v-if="sbDcPrice > 0">
          <dt>쇼핑북 할인</dt>
          <dd>
            <em>-{{ commaizeNumber(sbDcPrice) }}</em
            >원
          </dd>
        </dl>
        <dl v-if="marketingCoupon(goods!).length > 0">
          <dt>상품 {{ marketingCoupon(goods!)[0] }} 할인</dt>
          <dd>
            <em>-{{ marketingCoupon(goods!)[1] }}</em
            >원{{ goods?.detailInfo?.prcWaveDisp }}
          </dd>
        </dl>
        <dl v-if="lspPrice">
          <dt>일시불 할인</dt>
          <dd>
            <em>-{{ commaizeNumber(Number(goods?.promotionInfo?.lspAmt)) }}</em
            >원
          </dd>
        </dl>
        <dl v-if="goods?.promotionInfo?.cardBnftList && goods?.promotionInfo?.cardBnftList.length > 0">
          <dt>
            <button type="button" :class="isOpenCardBenefit(state.isShowToggle)" @click="_onClickToggleButton">
              {{ offerNm }}{{ amtStartVal }}
            </button>
          </dt>
          <dd v-if="cardBenefit">
            <em>-{{ realCardVal }}</em
            >원{{ goods?.detailInfo?.prcWaveDisp }}
          </dd>
        </dl>
        <div class="toggle-box" v-if="state.isShowToggle">
          <ul class="text-list">
            <li v-for="(item, index) in goods?.promotionInfo?.cardBnftList" :key="index">
              {{ cardOfferInfo(item) }}<span class="date">{{ `~${item.offerApplyEndDttm}` }}</span>
              <span class="option"> {{ cardOptionInfo(item) }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '~/pages/goods/scss/benefit-info.scss';
</style>
